import { useEffect, useRef, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { Row, TrackerSnapshot } from "@/lib/api";
import { QualityFlagsPill } from "@/components/QualityFlagsPill";
import {
  atrClass,
  dropClass,
  hitClass,
  posClass,
  retClass,
  riskClass,
  rrClass,
  rsClass,
  tickerBold,
  tickerTier,
  tierClass,
  trendClass,
  TREND_GLYPHS,
} from "@/lib/colors";
import { dollarCompact, money, percent, price, ratio, signedPercent } from "@/lib/format";

export type Mode = "setup" | "watch" | "all";

interface RankTableProps {
  rows: Row[];
  compact: boolean;
  mode: Mode;
  snapshots?: Record<string, TrackerSnapshot>;
  /** Tracker poll cadence in seconds (track_poll_seconds in viper,
   *  default 300). Used to render the "next poll in Xs" countdown
   *  above the table so the user can see the tracker is alive and
   *  predict when Live cells will refresh. Omitted → countdown is
   *  hidden. */
  pollIntervalSec?: number;
  onSelect?: (ticker: string) => void;
}

// Live-state palette — matches the Go tracker State enum. Applied to
// the ticker symbol cell as a subtle background tint, and to the Live
// column as the primary signal color.
const STATE_CELL_CLASS: Record<TrackerSnapshot["state"], string> = {
  idle: "",
  armed: "bg-amber-500/10",
  triggered: "bg-emerald-500/15",
  invalidated: "bg-rose-500/15",
};

const STATE_TEXT_CLASS: Record<TrackerSnapshot["state"], string> = {
  idle: "text-muted-foreground",
  armed: "text-amber-600 dark:text-amber-400",
  triggered: "text-emerald-600 dark:text-emerald-400 font-semibold",
  invalidated: "text-rose-600 dark:text-rose-400",
};

function changeClass(pct: number): string {
  if (!Number.isFinite(pct) || pct === 0) return "text-muted-foreground";
  return pct > 0
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-rose-600 dark:text-rose-400";
}

// formatAge and formatCountdown share the same compact "Xs / Xm Ys"
// representation. Split into two so the countdown can force a "in 0s"
// final tick without a sign flip confusing the caller.
function formatAge(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatCountdown(ms: number): string {
  return formatAge(ms);
}

export function RankTable({
  rows,
  compact,
  mode,
  snapshots,
  pollIntervalSec,
  onSelect,
}: RankTableProps) {
  const watch = mode === "watch";
  const hasTracker = !!snapshots && Object.keys(snapshots).length > 0;

  // Most-recent tracker poll across every snapshot. The tracker polls
  // all baselines in one pass, so the max updated_at is effectively
  // "the last time live data touched any row in this table". Using
  // the max (not any single ticker) makes the banner robust against
  // partial batches where one ticker errored and kept its old snap.
  const lastPollMs = (() => {
    if (!hasTracker) return 0;
    let maxMs = 0;
    for (const k in snapshots) {
      const t = Date.parse(snapshots[k]!.updated_at);
      if (Number.isFinite(t) && t > maxMs) maxMs = t;
    }
    return maxMs;
  })();

  // now is kept in state so the countdown and the flash fade both
  // tick every second. The setInterval is only wired when we actually
  // have tracker data — idle tables don't need a re-render loop.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!hasTracker) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [hasTracker]);

  // Per-ticker per-field "last changed at" timestamps. Used to drive
  // the flash-on-change highlight in the live and Δsup cells. Kept in
  // a ref so the diff pass doesn't re-render (the existing nowMs
  // interval handles the fade).
  const prevSnapshotsRef = useRef<Record<string, TrackerSnapshot>>({});
  const changedAtRef = useRef<
    Record<string, { live_price?: number; pct_from_support?: number }>
  >({});
  useEffect(() => {
    if (!snapshots) return;
    const now = Date.now();
    const prev = prevSnapshotsRef.current;
    for (const ticker in snapshots) {
      const cur = snapshots[ticker]!;
      const old = prev[ticker];
      if (!old) {
        // First arrival — don't flash; it's the initial render, not
        // a change the user should be drawn to.
        continue;
      }
      if (!changedAtRef.current[ticker]) changedAtRef.current[ticker] = {};
      if (old.live_price !== cur.live_price) {
        changedAtRef.current[ticker].live_price = now;
      }
      if (old.pct_from_support !== cur.pct_from_support) {
        changedAtRef.current[ticker].pct_from_support = now;
      }
    }
    prevSnapshotsRef.current = { ...snapshots };
  }, [snapshots]);

  // Window in ms that a changed cell stays highlighted before fading
  // back to its default background. 2 seconds is long enough to catch
  // the eye without staying visually busy if every row is flipping.
  const FLASH_MS = 2000;

  return (
    <div className="rounded-lg border bg-card">
      {hasTracker && lastPollMs > 0 && (
        <div className="flex items-center justify-between gap-4 border-b px-4 py-2 text-xs text-muted-foreground">
          <span>
            last poll:{" "}
            <span className="font-mono text-foreground">
              {new Date(lastPollMs).toLocaleTimeString()}
            </span>{" "}
            <span className="text-muted-foreground/70">
              ({formatAge(nowMs - lastPollMs)} ago)
            </span>
          </span>
          {pollIntervalSec ? (
            (() => {
              const nextMs = lastPollMs + pollIntervalSec * 1000;
              const remainingMs = nextMs - nowMs;
              const overdue = remainingMs < 0;
              return (
                <span>
                  next poll:{" "}
                  <span className="font-mono text-foreground">
                    {new Date(nextMs).toLocaleTimeString()}
                  </span>{" "}
                  <span
                    className={cn(
                      overdue
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground/70",
                    )}
                  >
                    ({overdue ? "overdue " : "in "}
                    {formatCountdown(Math.abs(remainingMs))})
                  </span>
                </span>
              );
            })()
          ) : null}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">ticker</TableHead>
            <TableHead className="text-right">close</TableHead>
            {hasTracker && <TableHead className="text-right">live</TableHead>}
            {hasTracker && <TableHead className="text-right">Δsup</TableHead>}
            {!compact && <TableHead className="text-right">support</TableHead>}
            {!compact && <TableHead className="text-right">resist</TableHead>}
            <TableHead className="text-right">hit</TableHead>
            <TableHead className="text-right">atr %</TableHead>
            <TableHead className="text-right" title="20-day average close × volume (liquidity)">
              $ vol
            </TableHead>
            <TableHead className="text-center">trend</TableHead>
            <TableHead className="text-right">rs</TableHead>
            <TableHead className="text-right">ret %</TableHead>
            <TableHead className="text-right">pos</TableHead>
            {!compact && <TableHead className="text-right">shares</TableHead>}
            <TableHead className="text-right">$ PnL</TableHead>
            <TableHead className="text-right">R:R</TableHead>
            <TableHead className="text-right">risk %</TableHead>
            {!compact && <TableHead className="text-right">stop</TableHead>}
            {!compact && <TableHead className="text-right">trail</TableHead>}
            {!compact && <TableHead className="text-left">bench</TableHead>}
            {watch && <TableHead className="text-right">trigger</TableHead>}
            {watch && <TableHead className="text-right">drop %</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const rangePct = r.support > 0 ? (r.resistance - r.support) / r.support : 0;
            const tierCls = tierClass(tickerTier(r.overall_score, r.hit_rate));
            const isBold = tickerBold(r.overall_score, r.hit_rate);
            const glyph = TREND_GLYPHS[r.trend] ?? "?";
            const snap = snapshots?.[r.ticker];
            const stateCellCls = snap ? STATE_CELL_CLASS[snap.state] : "";
            const changed = changedAtRef.current[r.ticker];
            const flashLive =
              changed?.live_price !== undefined &&
              nowMs - changed.live_price < FLASH_MS;
            const flashSup =
              changed?.pct_from_support !== undefined &&
              nowMs - changed.pct_from_support < FLASH_MS;
            // transition-colors drives the tint fade after the flash
            // window expires — the background flips from the flash
            // color back to the row's default, and CSS interpolates.
            const flashCls = "transition-colors duration-[1500ms]";
            return (
              <TableRow
                key={r.ticker}
                onClick={() => onSelect?.(r.ticker)}
                className={cn(onSelect && "cursor-pointer hover:bg-muted/40", stateCellCls)}
              >
                <TableCell className={cn("font-mono", tierCls, isBold && "font-bold")}>
                  {r.ticker}
                  <QualityFlagsPill flags={r.quality_flags} />
                </TableCell>
                <TableCell className="text-right font-mono">{price(r.current_price)}</TableCell>
                {hasTracker && (
                  <TableCell
                    className={cn(
                      "text-right font-mono",
                      flashCls,
                      flashLive && "bg-amber-500/25",
                      snap ? STATE_TEXT_CLASS[snap.state] : "text-muted-foreground"
                    )}
                  >
                    {snap ? (
                      <span>
                        ${price(snap.live_price)}{" "}
                        <span className={cn("text-xs", changeClass(snap.change_pct))}>
                          {signedPercent(snap.change_pct)}
                        </span>
                      </span>
                    ) : (
                      "–"
                    )}
                  </TableCell>
                )}
                {hasTracker && (
                  <TableCell
                    className={cn(
                      "text-right font-mono",
                      flashCls,
                      flashSup && "bg-amber-500/25",
                    )}
                  >
                    {snap ? (
                      <span className={changeClass(snap.pct_from_support)}>
                        {signedPercent(snap.pct_from_support)}
                      </span>
                    ) : (
                      "–"
                    )}
                  </TableCell>
                )}
                {!compact && (
                  <TableCell className="text-right font-mono">{price(r.support)}</TableCell>
                )}
                {!compact && (
                  <TableCell className="text-right font-mono">{price(r.resistance)}</TableCell>
                )}
                <TableCell className={cn("text-right font-mono", hitClass(r.hit_rate))}>
                  {percent(r.hit_rate)}
                </TableCell>
                <TableCell className={cn("text-right font-mono", atrClass(r.atr_pct))}>
                  {percent(r.atr_pct, 1)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-mono",
                    // Soft warning when liquidity sits below the scanner's
                    // $1M/day floor — a manually-added watchlist ticker
                    // that's too thin for reliable fills should be
                    // visually flagged even though the main pipeline
                    // doesn't filter it out.
                    r.avg_dollar_volume != null && r.avg_dollar_volume < 1_000_000
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-muted-foreground",
                  )}
                >
                  {dollarCompact(r.avg_dollar_volume)}
                </TableCell>
                <TableCell className={cn("text-center font-mono", trendClass(r.trend))}>
                  {glyph}
                </TableCell>
                <TableCell className={cn("text-right font-mono", rsClass(r.relative_strength))}>
                  {signedPercent(r.relative_strength)}
                </TableCell>
                <TableCell className={cn("text-right font-mono", retClass(r.expected_return_pct))}>
                  {percent(r.expected_return_pct, 1)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  <span className={posClass(r.current_position)}>
                    {percent(r.current_position)}
                  </span>{" "}
                  <span className="text-muted-foreground">of {percent(rangePct)}</span>
                </TableCell>
                {!compact && (
                  <TableCell className="text-right font-mono">{r.shares}</TableCell>
                )}
                <TableCell className={cn("text-right font-mono", retClass(r.expected_return_pct))}>
                  {money(r.expected_pnl)}
                </TableCell>
                <TableCell className={cn("text-right font-mono", rrClass(r.reward_risk))}>
                  {ratio(r.reward_risk)}
                </TableCell>
                <TableCell className={cn("text-right font-mono", riskClass(r.risk_pct))}>
                  {percent(r.risk_pct, 1)}
                </TableCell>
                {!compact && (
                  <TableCell className="text-right font-mono">{price(r.stop_loss)}</TableCell>
                )}
                {!compact && (
                  <TableCell className="text-right font-mono">
                    {price(r.trail_distance)}
                  </TableCell>
                )}
                {!compact && (
                  <TableCell className="text-muted-foreground">{r.benchmark || "–"}</TableCell>
                )}
                {watch && (
                  <TableCell className="text-right font-mono">{price(r.trigger)}</TableCell>
                )}
                {watch && (
                  <TableCell className={cn("text-right font-mono", dropClass(r.drop_pct))}>
                    {percent(r.drop_pct, 1)}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
